import React, { useState, useEffect, useRef } from 'react';
import { doc, updateDoc, collection, query, orderBy, limit, startAfter, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { getFunctions, httpsCallable } from 'firebase/functions';
import AdminAlertBanner from '../../components/admin/AdminAlertBanner';
import { COLORS } from '../../styles/tokens';
import { useIsMobile } from '../../hooks/useIsMobile';
import DriverDocumentsAdmin from './DriverDocumentsAdmin';
import { REQUIRED_KEYS, documentState } from '../../utils/driverDocuments';

const functions = getFunctions(undefined, 'europe-west1');
const approveUserRoleFn  = httpsCallable(functions, 'approveUserRole');
const rejectUserRoleFn   = httpsCallable(functions, 'rejectUserRole');
const freezeUserRoleFn   = httpsCallable(functions, 'freezeUserRole');
const unfreezeUserRoleFn = httpsCallable(functions, 'unfreezeUserRole');
const deleteUserFn       = httpsCallable(functions, 'deleteUser');
const setAdminClaimFn    = httpsCallable(functions, 'setAdminClaim');

const ROLE_LABEL = { driver: 'Sjåfør', orderer: 'Bestiller', admin: 'Admin' };
const NEUTRAL_CHIP = { color: COLORS.textLight, background: COLORS.surfaceHover };
const ROLE_CHIP = {
  driver:  { color: COLORS.blueText,   background: COLORS.blueBg },
  orderer: { color: COLORS.purpleText, background: COLORS.purpleBg },
  admin:   { color: COLORS.dangerText, background: COLORS.dangerBg },
};

const STATUS_CHIP = {
  pending:  { color: COLORS.warningText, background: COLORS.warningBg },
  active:   { color: COLORS.successText, background: COLORS.successBg },
  frozen:   { color: COLORS.blueText,    background: COLORS.blueBg },
  rejected: { color: COLORS.dangerText,  background: COLORS.dangerBg },
  deleted:  NEUTRAL_CHIP,
};
const STATUS_LABEL = {
  pending:  'Venter',
  active:   'Aktiv',
  frozen:   'Fryst',
  rejected: 'Avvist',
  deleted:  'Slettet',
};

// Dokumentstatus for en sjåfør: ventende (påkrevd eller kurs) og utløpte påkrevde dokumenter.
function documentFlags(user) {
  const docs = user.documents ?? {};
  const courses = Array.isArray(docs.courses) ? docs.courses : [];
  const pending = REQUIRED_KEYS.some(k => documentState(docs[k]) === 'pending')
    || courses.some(c => documentState(c) === 'pending');
  const expired = REQUIRED_KEYS.some(k => documentState(docs[k]) === 'expired');
  return { pending, expired };
}

export default function UserManager() {
  const [users,        setUsers]        = useState([]);
  const [lastDoc,      setLastDoc]      = useState(null);
  const [hasMore,      setHasMore]      = useState(true);
  const [loading,      setLoading]      = useState(false);
  const [search,       setSearch]       = useState('');
  const [roleFilter,   setRoleFilter]   = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [docsFilter,   setDocsFilter]   = useState(false);
  const [selected,    setSelected]     = useState(null);
  const [loadError,    setLoadError]    = useState('');
  const autoPages = useRef(0);
  const isMobile = useIsMobile();

  async function loadUsers(reset = false) {
    setLoading(true);
    setLoadError('');
    try {
      let q = query(collection(db, 'users'), orderBy('createdAt', 'desc'), limit(50));
      if (!reset && lastDoc) q = query(q, startAfter(lastDoc));

      const snap = await getDocs(q);
      const newUsers = snap.docs.map(d => ({ id: d.id, ...d.data() }));

      setUsers(prev => {
        const next = reset ? newUsers : [...prev, ...newUsers];
        setSelected(sel => {
          if (!sel) return sel;
          return next.find(u => u.id === sel.id) ?? sel;
        });
        return next;
      });
      setLastDoc(snap.docs[snap.docs.length - 1] ?? null);
      setHasMore(snap.docs.length === 50);
    } catch (e) {
      setLoadError('Kunne ikke laste brukere. Prøv igjen.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadUsers(true); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Dokumentfilteret er klientside: last alle gjenværende sider (med sikkerhetsgrense) mens det er på.
  const MAX_AUTO_PAGES = 40;
  useEffect(() => {
    if (!docsFilter) { autoPages.current = 0; return; }
    if (!hasMore || loading || loadError || autoPages.current >= MAX_AUTO_PAGES) return;
    autoPages.current += 1;
    loadUsers(false);
  }); // eslint-disable-line react-hooks/exhaustive-deps
  const loadingAll = docsFilter && hasMore && !loadError && autoPages.current < MAX_AUTO_PAGES;

  const filtered = users.filter(u => {
    const roles = u.roles ?? (u.role ? [u.role] : []);
    if (roleFilter && !roles.includes(roleFilter)) return false;
    if (statusFilter) {
      const driverMatch  = u.driverStatus  === statusFilter;
      const ordererMatch = u.ordererStatus === statusFilter;
      if (!driverMatch && !ordererMatch) return false;
    }
    if (docsFilter && !documentFlags(u).pending) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        u.name?.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q) ||
        u.orgNumber?.includes(q)
      );
    }
    return true;
  });

  return (
    <div style={styles.layout}>
      {/* Venstre: brukerliste */}
      {/* På mobil vises liste og detaljer hver for seg; listen skjules (ikke avmonteres) så rulleposisjonen beholdes */}
      <div style={{ ...styles.list, ...(isMobile ? { width: '100%', borderRight: 'none', display: selected ? 'none' : 'block' } : {}) }}>
        <div style={styles.filters}>
          <input
            style={styles.search}
            placeholder="Søk navn, e-post, org.nr…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <select style={styles.select} value={roleFilter} onChange={e => setRoleFilter(e.target.value)}>
            <option value="">Alle roller</option>
            <option value="driver">Sjåfører</option>
            <option value="orderer">Bestillere</option>
            <option value="admin">Admin</option>
          </select>
          <select style={styles.select} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="">Alle statuser</option>
            <option value="pending">Venter godkjenning</option>
            <option value="active">Aktiv</option>
            <option value="frozen">Fryst</option>
            <option value="rejected">Avvist</option>
            <option value="deleted">Slettet</option>
          </select>
          <label style={{ fontSize: 13, color: COLORS.textDark, display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={docsFilter} onChange={e => setDocsFilter(e.target.checked)} />
            Dokumenter til godkjenning
          </label>
        </div>

        <div style={styles.count}>{filtered.length} brukere</div>
        {loadingAll && <p style={{ padding: '4px 16px', color: COLORS.textLight, fontSize: 13 }}>Laster alle brukere…</p>}

        {loading && users.length === 0 && <p style={{ padding: '20px', color: COLORS.textLight }}>Laster brukere…</p>}
        {loadError && <p style={{ padding: '10px 16px', color: COLORS.dangerText, fontSize: 13 }}>{loadError}</p>}

        {filtered.map(user => (
          <UserRow
            key={user.id}
            user={user}
            selected={selected?.id === user.id}
            onClick={() => setSelected(user)}
          />
        ))}

        {hasMore && (
          <button onClick={() => loadUsers(false)} disabled={loading} style={{ width: '100%', padding: 10, background: 'transparent', color: COLORS.textDark, border: `1px solid ${COLORS.border}`, borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
            {loading ? 'Laster…' : 'Last inn flere'}
          </button>
        )}
      </div>

      {/* Høyre: brukerdetaljer */}
      <div style={{ ...styles.detail, display: isMobile && !selected ? 'none' : 'block' }}>
        {isMobile && selected && (
          <button onClick={() => setSelected(null)} style={styles.backBtn}>← Tilbake til listen</button>
        )}
        {!selected ? (
          <div style={styles.emptyDetail}>
            <div style={{ fontSize: 48 }}>👤</div>
            <p style={{ marginTop: 16, color: COLORS.textLight }}>Velg en bruker for å se detaljer</p>
          </div>
        ) : (
          <UserDetail
            user={selected}
            onUpdate={updated => setSelected(updated)}
            onActionComplete={() => loadUsers(true)}
          />
        )}
      </div>
    </div>
  );
}

function UserRow({ user, selected, onClick }) {
  const docFlags = user.driverStatus ? documentFlags(user) : { pending: false, expired: false };
  return (
    <div onClick={onClick} style={{
      ...styles.userRow,
      background: selected ? COLORS.surfaceHover : COLORS.white,
      borderLeft: selected ? `4px solid ${COLORS.primary}` : '4px solid transparent',
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {user.name ?? user.email}
        </div>
        <div style={{ fontSize: 12, color: COLORS.textLight, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {user.email}
        </div>
      </div>
      <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
        {/* Roles */}
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {(user.roles ?? (user.role ? [user.role] : [])).map(r => (
            <span key={r} style={{
              fontSize: 11, fontWeight: 700,
              ...(ROLE_CHIP[r] ?? NEUTRAL_CHIP),
              padding: '2px 8px', borderRadius: 4,
            }}>
              {ROLE_LABEL[r] ?? r}
            </span>
          ))}
        </div>
        {/* Per-role statuses */}
        {user.driverStatus && (
          <span style={{ fontSize: 11, fontWeight: 700, ...(STATUS_CHIP[user.driverStatus] ?? NEUTRAL_CHIP), padding: '2px 8px', borderRadius: 4 }}>
            Sjåfør: {STATUS_LABEL[user.driverStatus] ?? user.driverStatus}
          </span>
        )}
        {user.ordererStatus && (
          <span style={{ fontSize: 11, fontWeight: 700, ...(STATUS_CHIP[user.ordererStatus] ?? NEUTRAL_CHIP), padding: '2px 8px', borderRadius: 4 }}>
            Bestiller: {STATUS_LABEL[user.ordererStatus] ?? user.ordererStatus}
          </span>
        )}
        {docFlags.pending && (
          <span style={{ fontSize: 11, fontWeight: 700, color: COLORS.warningText, background: COLORS.warningBg, padding: '2px 8px', borderRadius: 4 }}>Dok. venter</span>
        )}
        {docFlags.expired && (
          <span style={{ fontSize: 11, fontWeight: 700, color: COLORS.dangerText, background: COLORS.dangerBg, padding: '2px 8px', borderRadius: 4 }}>Dok. utløpt</span>
        )}
        {user.isTestAccount && (
          <span style={{ fontSize: 10, fontWeight: 700, color: COLORS.textLight, background: COLORS.surfaceHover, padding: '2px 6px', borderRadius: 4 }}>TEST</span>
        )}
      </div>
    </div>
  );
}

function UserDetail({ user, onUpdate, onActionComplete }) {
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  async function toggle(field) {
    setSaving(true);
    setError('');
    try {
      const newValue = !user[field];
      await updateDoc(doc(db, 'users', user.id), { [field]: newValue });
      onUpdate({ ...user, [field]: newValue });
    } catch {
      setError('Kunne ikke oppdatere. Prøv igjen.');
    } finally {
      setSaving(false);
    }
  }

  const createdAt = user.createdAt?.toDate?.();
  const isMobile = useIsMobile();

  return (
    <div style={{ ...styles.detailContent, ...(isMobile ? { padding: 16 } : {}) }}>
      <div style={styles.userHeader}>
        <div style={styles.avatar}>{(user.name ?? user.email ?? '?')[0].toUpperCase()}</div>
        <div>
          <h2 style={styles.userName}>{user.name ?? '—'}</h2>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            {(user.roles ?? (user.role ? [user.role] : [])).map(r => (
              <span key={r} style={{ fontSize: 12, fontWeight: 700, ...(ROLE_CHIP[r] ?? NEUTRAL_CHIP), padding: '3px 10px', borderRadius: 4 }}>
                {ROLE_LABEL[r] ?? r}
              </span>
            ))}
            {user.driverStatus && (
              <span style={{ fontSize: 12, fontWeight: 700, ...(STATUS_CHIP[user.driverStatus] ?? NEUTRAL_CHIP), padding: '3px 10px', borderRadius: 4 }}>
                Sjåfør: {STATUS_LABEL[user.driverStatus]}
              </span>
            )}
            {user.ordererStatus && (
              <span style={{ fontSize: 12, fontWeight: 700, ...(STATUS_CHIP[user.ordererStatus] ?? NEUTRAL_CHIP), padding: '3px 10px', borderRadius: 4 }}>
                Bestiller: {STATUS_LABEL[user.ordererStatus]}
              </span>
            )}
          </div>
        </div>
      </div>

      <AdminAlertBanner entityId={user.id} />

      <Section title="Kontaktinfo">
        <Grid>
          <Field label="E-post"  value={user.email} />
          <Field label="Telefon" value={user.phone ?? '—'} />
          <Field label="Org.nr." value={user.orgNumber ?? '—'} />
          <Field label="Firma"   value={user.orgName ?? '—'} />
          {createdAt && <Field label="Registrert" value={createdAt.toLocaleDateString('nb-NO', { dateStyle: 'medium' })} />}
        </Grid>
      </Section>

      {user.driverStatus && (
        <Section title="Dokumentasjon">
          <DriverDocumentsAdmin user={user} onChanged={onActionComplete} />
        </Section>
      )}

      <Section title="Betalingsstatus">
        <Grid>
          <Field label="Stripe Customer ID"  value={user.stripeCustomerId  ?? 'Ikke opprettet'} />
          <Field label="Stripe Connect ID"   value={user.stripeAccountId   ?? 'Ikke opprettet'} />
        </Grid>
      </Section>

      <Section title="Handlinger">
        <ActionBar user={user} onActionComplete={onActionComplete} />
      </Section>

      <Section title="Administrasjon">
        <div style={styles.toggleList}>
          <ToggleRow
            label="Testkonto"
            description="Alle bud og ordre fra denne kontoen ekskluderes fra priskalibrering"
            checked={!!user.isTestAccount}
            onToggle={() => toggle('isTestAccount')}
            disabled={saving}
          />
          {(user.roles ?? []).includes('orderer') && (
            <>
              <ToggleRow
                label="Kredittvurdert"
                description="Bestilleren har bestått kredittvurdering"
                checked={!!user.creditVerified}
                onToggle={() => toggle('creditVerified')}
                disabled={saving}
              />
              <ToggleRow
                label="Forhåndsbetaling påkrevd"
                description="Bestilleren må forhåndsbetale for hvert oppdrag"
                checked={!!user.prepaymentRequired}
                onToggle={() => toggle('prepaymentRequired')}
                disabled={saving}
              />
            </>
          )}
        </div>
        {error && <p style={styles.error}>{error}</p>}
      </Section>
    </div>
  );
}

function ActionBar({ user, onActionComplete }) {
  const [reason, setReason] = useState('');
  const [busy,   setBusy]   = useState(false);
  const [error,  setError]  = useState('');

  async function callFn(fn, args, fnName) {
    if (!reason.trim()) { setError('Skriv en begrunnelse'); return; }
    setBusy(true);
    setError('');
    try {
      await fn({ ...args, reason });
      onActionComplete();
    } catch (e) {
      setError(e.message ?? `Feil ved ${fnName}`);
    } finally {
      setBusy(false);
    }
  }

  const hasActiveRole = user.driverStatus === 'active' || user.ordererStatus === 'active';

  return (
    <div>
      <textarea
        placeholder="Begrunnelse (påkrevd)…"
        value={reason}
        onChange={e => setReason(e.target.value)}
        style={{ width: '100%', padding: 8, borderRadius: 6, border: `1px solid ${COLORS.border}`, marginBottom: 10, fontSize: 13, boxSizing: 'border-box', background: COLORS.white, color: COLORS.textDark }}
        rows={2}
      />
      {error && <p style={{ color: COLORS.dangerText, fontSize: 13, marginBottom: 8 }}>{error}</p>}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {/* Driver role actions */}
        {user.driverStatus === 'pending' && (
          <>
            <ActionBtn color={COLORS.success} disabled={busy} onClick={() => callFn(approveUserRoleFn, { uid: user.id, role: 'driver' }, 'godkjenning sjåfør')}>Godkjenn sjåfør</ActionBtn>
            <ActionBtn color={COLORS.danger} disabled={busy} onClick={() => callFn(rejectUserRoleFn,  { uid: user.id, role: 'driver' }, 'avvisning sjåfør')}>Avvis sjåfør</ActionBtn>
          </>
        )}
        {user.driverStatus === 'active' && (
          <ActionBtn color={COLORS.blue} disabled={busy} onClick={() => callFn(freezeUserRoleFn, { uid: user.id, role: 'driver' }, 'frysing sjåfør')}>Frys sjåfør</ActionBtn>
        )}
        {user.driverStatus === 'frozen' && (
          <ActionBtn color={COLORS.success} disabled={busy} onClick={() => callFn(unfreezeUserRoleFn, { uid: user.id, role: 'driver' }, 'opptøing sjåfør')}>Opptø sjåfør</ActionBtn>
        )}

        {/* Orderer role actions */}
        {user.ordererStatus === 'pending' && (
          <>
            <ActionBtn color={COLORS.success} disabled={busy} onClick={() => callFn(approveUserRoleFn, { uid: user.id, role: 'orderer' }, 'godkjenning bestiller')}>Godkjenn bestiller</ActionBtn>
            <ActionBtn color={COLORS.danger} disabled={busy} onClick={() => callFn(rejectUserRoleFn,  { uid: user.id, role: 'orderer' }, 'avvisning bestiller')}>Avvis bestiller</ActionBtn>
          </>
        )}
        {user.ordererStatus === 'active' && (
          <ActionBtn color={COLORS.blue} disabled={busy} onClick={() => callFn(freezeUserRoleFn, { uid: user.id, role: 'orderer' }, 'frysing bestiller')}>Frys bestiller</ActionBtn>
        )}
        {user.ordererStatus === 'frozen' && (
          <ActionBtn color={COLORS.success} disabled={busy} onClick={() => callFn(unfreezeUserRoleFn, { uid: user.id, role: 'orderer' }, 'opptøing bestiller')}>Opptø bestiller</ActionBtn>
        )}

        {/* Account-level actions */}
        {!hasActiveRole && (
          <ActionBtn color={COLORS.danger} disabled={busy} onClick={() => callFn(deleteUserFn, { uid: user.id }, 'sletting')}>Slett bruker</ActionBtn>
        )}
        {!(user.roles ?? []).includes('admin') && (
          <ActionBtn color={COLORS.primary} disabled={busy} onClick={() => callFn(setAdminClaimFn, { uid: user.id }, 'admin-tildeling')}>Gi admin-rolle</ActionBtn>
        )}
      </div>
    </div>
  );
}

function ActionBtn({ color, children, onClick, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{ padding: '8px 16px', background: color, color: '#fff', border: 'none', borderRadius: 6, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1, fontSize: 13, fontWeight: 600 }}
    >
      {children}
    </button>
  );
}

// ── Hjelpere ─────────────────────────────────────────────────────────────────

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <h3 style={{ fontSize: 13, fontWeight: 700, color: COLORS.textLight, textTransform: 'uppercase', margin: '0 0 12px' }}>{title}</h3>
      {children}
    </div>
  );
}

function Grid({ children }) {
  const isMobile = useIsMobile();
  return <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '10px 24px' }}>{children}</div>;
}

function Field({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: COLORS.textLight, fontWeight: 600, textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: 14, color: COLORS.textDark, marginTop: 2, wordBreak: 'break-all' }}>{value ?? '—'}</div>
    </div>
  );
}

function ToggleRow({ label, description, checked, onToggle, disabled }) {
  return (
    <div style={styles.toggleRow}>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600, fontSize: 14 }}>{label}</div>
        <div style={{ fontSize: 12, color: COLORS.textLight, marginTop: 2 }}>{description}</div>
      </div>
      <button
        onClick={onToggle}
        disabled={disabled}
        style={{
          ...styles.toggle,
          background: checked ? COLORS.success : COLORS.border,
        }}
      >
        <div style={{
          width: 18, height: 18, borderRadius: '50%', background: '#fff',
          transform: checked ? 'translateX(18px)' : 'translateX(2px)',
          transition: 'transform 0.2s',
        }} />
      </button>
    </div>
  );
}

const styles = {
  layout:        { display: 'flex', height: '100%' },
  list:          { width: 320, borderRight: `1px solid ${COLORS.border}`, overflowY: 'auto', flexShrink: 0 },
  filters:       { padding: '14px 16px', borderBottom: `1px solid ${COLORS.border}`, display: 'flex', flexDirection: 'column', gap: 8, background: COLORS.white },
  search:        { padding: '8px 12px', border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 14, width: '100%', boxSizing: 'border-box', background: COLORS.white, color: COLORS.textDark },
  select:        { padding: '7px 10px', border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 13, background: COLORS.white, color: COLORS.textDark },
  count:         { padding: '8px 16px', fontSize: 12, color: COLORS.textLight, borderBottom: `1px solid ${COLORS.border}` },
  userRow:       { padding: '12px 20px', cursor: 'pointer', borderBottom: `1px solid ${COLORS.border}`, display: 'flex', alignItems: 'center', gap: 12 },
  detail:        { flex: 1, overflowY: 'auto' },
  backBtn:       { background: 'none', border: 'none', color: COLORS.blue, cursor: 'pointer', fontSize: 14, padding: '14px 16px 0' },
  detailContent: { padding: '28px 32px', maxWidth: 600 },
  emptyDetail:   { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: COLORS.textLight },
  userHeader:    { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 28 },
  avatar:        { width: 52, height: 52, borderRadius: '50%', background: COLORS.primary, color: COLORS.onPrimary, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, fontWeight: 700, flexShrink: 0 },
  userName:      { fontSize: 20, fontWeight: 700, color: COLORS.textDark, margin: '0 0 6px' },
  toggleList:    { display: 'flex', flexDirection: 'column', gap: 12 },
  toggleRow:     { display: 'flex', alignItems: 'center', gap: 16, padding: '12px 16px', background: COLORS.surfaceHover, borderRadius: 8 },
  toggle:        { width: 40, height: 22, borderRadius: 11, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', transition: 'background 0.2s', padding: 0, flexShrink: 0 },
  error:         { color: COLORS.dangerText, fontSize: 13, marginTop: 8 },
};
